import { useState } from "react";
import { Link } from "react-router-dom";
import { FREQUENCIA, casaNaBusca, cnpjNaTela, cpfNaTela, dataHora, type Painel } from "../lib/sentinela";
import { DonoDoAlvo, Numero, Selo, SeloCriticidade, SeloRisco } from "./SentinelaUi";

/**
 * O painel de terceiros e a lista de CPFs monitorados (as telas `/dashboard` e `/cpfs` de lá), sobre a MESMA
 * leitura (`sentinela_painel`): as duas listas e os números dos cartões vêm prontos do banco, e a tela só busca e
 * esconde os arquivados. Na leitura da equipe, cada linha diz de quem é o alvo.
 *
 * Os dois defeitos da busca de lá não vieram (`casaNaBusca`): o termo sem dígito casava todo cadastro, e na
 * lista de CPFs a busca pelo CPF formatado não achava nada. E o terceiro arquivado ganhou a marca que lá só a
 * lista de CPFs tinha.
 */

const CAMPO =
  "min-h-11 w-full rounded-cf border border-borda bg-superficie px-3 text-sm text-tinta placeholder:text-cinza focus:border-acento focus:outline-none focus:ring-2 focus:ring-acento/30";

function Arquivados({ n, rotulo, mostrar, setMostrar }: { n: number; rotulo: string; mostrar: boolean; setMostrar: (v: boolean) => void }) {
  if (n === 0) return null;
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-cf border border-borda bg-chip px-4 py-2 text-sm text-tinta" data-faixa-arquivados>
      <span>
        {n} {rotulo} — a trilha de auditoria continua guardada.
      </span>
      <label className="flex items-center gap-2">
        <input type="checkbox" checked={mostrar} onChange={(e) => setMostrar(e.target.checked)} className="size-4 accent-acento" />
        Mostrar arquivados
      </label>
    </div>
  );
}

function Quando({ proxima, ultima, frequencia, monitorado }: { proxima: string | null; ultima: string | null; frequencia: string; monitorado: boolean }) {
  return (
    <div className="hidden text-right text-xs text-cinza sm:block">
      <div>Próx.: {monitorado && proxima ? dataHora(proxima) : (FREQUENCIA[frequencia] ?? frequencia)}</div>
      <div>Última: {ultima ? dataHora(ultima) : "nunca"}</div>
    </div>
  );
}

export function ListaDeEmpresas({ painel, base }: { painel: Painel; base: string }) {
  const [busca, setBusca] = useState("");
  const [mostrar, setMostrar] = useState(false);
  const n = painel.numeros_empresas;
  const equipe = painel.escopo === "equipe";
  const lista = painel.empresas.filter(
    (e) => (mostrar || !e.arquivado_em) && casaNaBusca(busca, [e.razao_social, e.nome_fantasia, e.dono?.nome, e.dono?.email], e.cnpj),
  );

  return (
    <section className="flex flex-col gap-4" data-sentinela-empresas>
      <div>
        <h3 className="font-display text-xl font-bold text-titulo">Painel de terceiros</h3>
        <p className="mt-1 text-sm text-cinza">
          Diligência e monitoramento de fornecedores e parceiros.
          {equipe && " Aqui estão os terceiros de todos os alunos, com quem cadastrou cada um."}
        </p>
      </div>
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Numero rotulo="Terceiros cadastrados" valor={n.ativas} atributo="ativas" />
        <Numero rotulo="Risco alto" valor={n.risco_alto} tom={n.risco_alto > 0 ? "erro" : undefined} atributo="risco-alto" />
        <Numero rotulo="Varreduras vencidas" valor={n.vencidas} tom={n.vencidas > 0 ? "alerta" : undefined} atributo="vencidas" />
        <Numero rotulo="Alertas não lidos" valor={painel.alertas_nao_lidos} tom={painel.alertas_nao_lidos > 0 ? "erro" : undefined} atributo="alertas" />
      </div>
      <label className="block">
        <span className="sr-only">Buscar terceiros</span>
        <input
          type="search"
          value={busca}
          onChange={(e) => setBusca(e.target.value)}
          placeholder={equipe ? "Buscar por razão social, nome fantasia, CNPJ ou aluno…" : "Buscar por razão social, nome fantasia ou CNPJ…"}
          className={CAMPO}
          data-busca-empresas
        />
      </label>
      <Arquivados n={n.arquivadas} rotulo={n.arquivadas === 1 ? "terceiro arquivado" : "terceiros arquivados"} mostrar={mostrar} setMostrar={setMostrar} />
      {lista.length === 0 ? (
        <p className="surface rounded-cf p-6 text-sm text-cinza" data-lista-vazia>
          {painel.empresas.length === 0 ? "Nenhum terceiro cadastrado." : "Nenhum terceiro encontrado com esse filtro."}
        </p>
      ) : (
        <ul className="surface divide-y divide-borda rounded-cf" data-lista-empresas>
          {lista.map((e) => (
            <li key={e.id}>
              <Link
                to={`${base}?empresa=${e.id}`}
                className="flex flex-wrap items-center gap-3 px-4 py-3 transition hover:bg-chip sm:flex-nowrap"
                data-linha-empresa={e.id}
              >
                <div className="min-w-0 flex-1">
                  <div className="truncate font-semibold text-titulo" title={e.razao_social ?? undefined}>
                    {e.razao_social || "—"}
                  </div>
                  <div className="truncate text-xs text-cinza">
                    {cnpjNaTela(e.cnpj)} · {e.municipio || "—"}/{e.uf || "—"} · {e.situacao_cadastral || "—"}
                  </div>
                  <DonoDoAlvo dono={e.dono} />
                </div>
                <Quando proxima={e.proxima_varredura_em} ultima={e.ultima_varredura_em} frequencia={e.frequencia} monitorado={e.monitorado} />
                <div className="flex flex-wrap gap-1.5">
                  {e.arquivado_em && <Selo tom="neutro" atributo="arquivado">Arquivado</Selo>}
                  <SeloCriticidade valor={e.criticidade} />
                  <SeloRisco valor={e.nivel_de_risco} />
                </div>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

export function ListaDeCpfs({ painel, base }: { painel: Painel; base: string }) {
  const [busca, setBusca] = useState("");
  const [mostrar, setMostrar] = useState(false);
  const n = painel.numeros_cpfs;
  const equipe = painel.escopo === "equipe";
  const lista = painel.cpfs.filter((c) => (mostrar || !c.arquivado_em) && casaNaBusca(busca, [c.nome, c.dono?.nome, c.dono?.email], c.cpf));

  return (
    <section className="flex flex-col gap-4" data-sentinela-cpfs>
      <div>
        <h3 className="font-display text-xl font-bold text-titulo">CPFs monitorados</h3>
        <p className="mt-1 text-sm text-cinza">
          Monitoramento avulso de pessoas físicas.
          {equipe && " Aqui estão os CPFs de todos os alunos, com quem cadastrou cada um."}
        </p>
      </div>
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Numero rotulo="CPFs monitorados" valor={n.ativos} atributo="ativos" />
        <Numero rotulo="Risco alto" valor={n.risco_alto} tom={n.risco_alto > 0 ? "erro" : undefined} atributo="risco-alto" />
        <Numero rotulo="Varreduras vencidas" valor={n.vencidas} tom={n.vencidas > 0 ? "alerta" : undefined} atributo="vencidas" />
        <Numero
          rotulo="Alertas não lidos (de CPF)"
          valor={painel.alertas_nao_lidos_cpf}
          tom={painel.alertas_nao_lidos_cpf > 0 ? "erro" : undefined}
          atributo="alertas"
        />
      </div>
      <label className="block">
        <span className="sr-only">Buscar CPFs</span>
        <input
          type="search"
          value={busca}
          onChange={(e) => setBusca(e.target.value)}
          placeholder={equipe ? "Buscar por nome, CPF ou aluno…" : "Buscar por nome ou CPF…"}
          className={CAMPO}
          data-busca-cpfs
        />
      </label>
      <Arquivados n={n.arquivados} rotulo={n.arquivados === 1 ? "cadastro arquivado" : "cadastros arquivados"} mostrar={mostrar} setMostrar={setMostrar} />
      {lista.length === 0 ? (
        <p className="surface rounded-cf p-6 text-sm text-cinza" data-lista-vazia>
          {painel.cpfs.length === 0 ? "Nenhum CPF cadastrado." : "Nenhum CPF encontrado com esse filtro."}
        </p>
      ) : (
        <ul className="surface divide-y divide-borda rounded-cf" data-lista-cpfs>
          {lista.map((c) => (
            <li key={c.id}>
              <Link to={`${base}?cpf=${c.id}`} className="flex flex-wrap items-center gap-3 px-4 py-3 transition hover:bg-chip sm:flex-nowrap" data-linha-cpf={c.id}>
                <div className="min-w-0 flex-1">
                  <div className="truncate font-semibold text-titulo">{c.nome}</div>
                  <div className="truncate text-xs text-cinza">
                    {cpfNaTela(c.cpf)} · última: {c.ultima_varredura_em ? dataHora(c.ultima_varredura_em) : "nunca"}
                  </div>
                  <DonoDoAlvo dono={c.dono} />
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {c.arquivado_em && <Selo tom="neutro" atributo="arquivado">Arquivado</Selo>}
                  <SeloCriticidade valor={c.criticidade} />
                  <SeloRisco valor={c.nivel_de_risco} />
                </div>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
